"use client";

import { FC, useEffect, useState } from "react";
import TextTitle from "apps/website/components/base/Text/TextTitle/TextTitle";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import { CTAButton } from "apps/website/components/base/CTAButton/CTAButton";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import useFocusTrap from "apps/website/hooks/useFocusTrap";
import useModal from "apps/website/hooks/useModal";
import Container from "apps/website/components/layout/Container/Container";
import Column from "apps/website/components/layout/Column/Column";
import Icon from "apps/website/components/base/Icon/Icon";
import Sticker from "apps/website/components/base/Sticker/Sticker";
import { STICKER_VARIANTS } from "apps/website/components/base/Sticker/types";

import { DisplayState } from "@/constants/state";

import Modal from "../Modal";

import {
  INibblesPouchesUpsellItem,
  NIBBLES_POUCHES_UPSELL_MODAL_ADD_TO_ORDER_BUTTON_TEXT,
  NIBBLES_POUCHES_UPSELL_MODAL_CARD_TAG,
  NIBBLES_POUCHES_UPSELL_MODAL_DESCRIPTION,
  NIBBLES_POUCHES_UPSELL_MODAL_FOOTER_TEXT,
  NIBBLES_POUCHES_UPSELL_MODAL_IMAGE,
  NIBBLES_POUCHES_UPSELL_MODAL_ITEMS,
  NIBBLES_POUCHES_UPSELL_MODAL_PICK_OWN_BUTTON_TEXT,
  NIBBLES_POUCHES_UPSELL_MODAL_PRICE_AFTER_DISCOUNT,
  NIBBLES_POUCHES_UPSELL_MODAL_PRICE_BEFORE_DISCOUNT,
  NIBBLES_POUCHES_UPSELL_MODAL_SUBHEADING,
  NIBBLES_POUCHES_UPSELL_MODAL_SUCCESS_IMAGE,
  NIBBLES_POUCHES_UPSELL_MODAL_SUCCESS_TEXT,
  NIBBLES_POUCHES_UPSELL_MODAL_TITLE,
} from "./NibblesPouchesUpsellModal.constants";

export type NibblesPouchesPurchaseState = "purchased" | "processing" | "failure" | "none";

export interface INibblesPouchesUpsellModalProps {
  id: string;
  isOpen: boolean;
  onClose(): void;
  onConfirm(): void;
  onPickOwn(): void;
  theme?: Theme;
  title?: string;
  subheading?: string;
  description?: string;
  image?: IImageProps;
  items?: INibblesPouchesUpsellItem[];
  priceBeforeDiscount?: number;
  priceAfterDiscount?: number;
  footerText?: string;
  addToOrderButtonText?: string;
  pickOwnButtonText?: string;
  result?: NibblesPouchesPurchaseState;
  successText?: string;
  successImage?: IImageProps;
  cardTag?: string;
}

const NibblesPouchesUpsellModal: FC<INibblesPouchesUpsellModalProps> = ({
  id,
  isOpen,
  onClose,
  onConfirm,
  onPickOwn,
  theme = "light-grey",
  title = NIBBLES_POUCHES_UPSELL_MODAL_TITLE,
  subheading = NIBBLES_POUCHES_UPSELL_MODAL_SUBHEADING,
  description = NIBBLES_POUCHES_UPSELL_MODAL_DESCRIPTION,
  image = NIBBLES_POUCHES_UPSELL_MODAL_IMAGE,
  items = NIBBLES_POUCHES_UPSELL_MODAL_ITEMS,
  priceBeforeDiscount = NIBBLES_POUCHES_UPSELL_MODAL_PRICE_BEFORE_DISCOUNT,
  priceAfterDiscount = NIBBLES_POUCHES_UPSELL_MODAL_PRICE_AFTER_DISCOUNT,
  footerText = NIBBLES_POUCHES_UPSELL_MODAL_FOOTER_TEXT,
  addToOrderButtonText = NIBBLES_POUCHES_UPSELL_MODAL_ADD_TO_ORDER_BUTTON_TEXT,
  pickOwnButtonText = NIBBLES_POUCHES_UPSELL_MODAL_PICK_OWN_BUTTON_TEXT,
  result,
  successText = NIBBLES_POUCHES_UPSELL_MODAL_SUCCESS_TEXT,
  successImage = NIBBLES_POUCHES_UPSELL_MODAL_SUCCESS_IMAGE,
  cardTag = NIBBLES_POUCHES_UPSELL_MODAL_CARD_TAG,
}) => {
  const { modalId, modalTitleId, modalDescriptionId, modalButtonId, modalIsOpen, setModalIsOpen } = useModal(isOpen);
  const { initFocusTrap, destroyFocusTrap } = useFocusTrap();
  const [ buttonState, setButtonState ] = useState(DisplayState.READY);

  useEffect(() => {
    if (result === "purchased") {
      setButtonState(DisplayState.COMPLETE);
    } else if (result === "processing") {
      setButtonState(DisplayState.LOADING);
    } else if (result === "failure") {
      setButtonState(DisplayState.ERROR);
    }
  }, [ result ]);

  useEffect(() => {
    if (modalIsOpen) {
      initFocusTrap(modalId);
    } else {
      destroyFocusTrap(modalButtonId);
      if (onClose) {
        onClose();
      }
    }
  }, [ destroyFocusTrap, initFocusTrap, modalButtonId, modalId, modalIsOpen, onClose ]);

  return (
    <Modal
      id={id}
      component={NibblesPouchesUpsellModal.name}
      isOpen={isOpen}
      onClose={() => { setModalIsOpen(false); }}
      modalTitleId={modalTitleId}
      modalDescriptionId={modalDescriptionId}
      hideCloseButton={false}
      size="sm"
      theme={theme}
    >
      <Container
        className="flex flex-col p-5 lg:p-6 items-center justify-center align-middle"
      >
        { result !== "purchased" ? (
          <>
            <TextSubtitle data-title size={1} align="center">
              { title }
            </TextSubtitle>
            <Spacer size="md" />
            <div className="w-full bg-white">
              <div className="relative">
                <Image image={image} alt="Nibbles Trio Bundle"></Image>
                { cardTag && (
                  <div className="absolute -top-8 -left-6 md:-top-10 md:-left-8 z-10">
                    <Sticker variant={STICKER_VARIANTS.FIFTY_OFF} size="sm" mdSize="md" />
                  </div>
                ) }
              </div>
              <div className="flex flex-col items-center p-4">
                <TextSubtitle align="center" size={3}>
                  { subheading }
                </TextSubtitle>
                <Spacer size="sm" />
                <TextBody className="!mb-0" align="center" size={3}>
                  { description }
                </TextBody>
                <Spacer size="sm" />
                <ul className="list-none text-center" data-testid="nibbles-pouches-items">
                  { items.map((item) => (
                    <li key={item.id}>
                      <TextBody tag="span" align="center" size={3}>{ item.content }</TextBody>
                    </li>
                  )) }
                </ul>
                <Spacer size="sm" />
                <div className="flex gap-2 items-baseline justify-center">
                  <TextBody
                    tag="span"
                    align="center"
                    size={3}
                    className="line-through"
                  >
                    { `£${priceBeforeDiscount.toFixed(2)}` }
                  </TextBody>
                  <TextSubtitle align="center" size={3}>
                    { `£${priceAfterDiscount.toFixed(2)}` }
                  </TextSubtitle>
                </div>
                <Spacer size="sm" />
                <TextBody align="center" size={3}>
                  { footerText }
                </TextBody>
              </div>
            </div>
            <Spacer size="lg" />
            <Container dataTestId="nibbles-pouches-ctas" className="flex flex-col sm:flex-row gap-2 items-center justify-center w-full">
              <CTAButton
                text={addToOrderButtonText.toUpperCase()}
                color="secondary"
                onClick={onConfirm}
                state={buttonState}
              />
              <CTAButton
                text={pickOwnButtonText}
                design="underline"
                onClick={onPickOwn}
              />
            </Container>
            <Spacer size="lg" />
          </>
        ) : (
          <Column className="min-h-96 items-center justify-center text-center px-4">
            { successImage && (
              <div className="w-1/2">
                <Image image={successImage} alt="Nibbles Trio Bundle"></Image>
              </div>
            ) }
            <div className={`px-2 lg:px-4 h-11 flex justify-center items-center ${themeRootClassMap.green}`} data-theme="green" data-testid="alwaysInBox">
              <Icon icon="tickHeavy" size="small" />
              <TextTitle tag="span" size={3}>
                In your box
              </TextTitle>
            </div>
            <Spacer size="lg" />
            <TextSubtitle align="center" size={2}>
              { successText }
            </TextSubtitle>
            <Spacer size="lg" />
            <Spacer size="lg" />
          </Column>
        ) }
      </Container>
    </Modal>
  );
};

export default NibblesPouchesUpsellModal;
